import { useEffect, useState } from 'react';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { ShutdownRuleForm } from '../shared/ShutdownRuleForm';
import { VoteRoomShutdownSettings } from '../shared/voteRoomShutdown';

type ShutdownRuleStepProps = {
  settings: VoteRoomShutdownSettings;
  onChange: (settings: VoteRoomShutdownSettings) => void;
  locked: boolean;
  saving?: boolean;
  onBack: () => void;
  onContinue: (settings: VoteRoomShutdownSettings) => void | Promise<void>;
};

/**
 * Third wizard step: choose when the Vote Room auto-closes. Settings are owned by the parent and
 * locked after launch.
 */
export const ShutdownRuleStep = ({
  settings,
  onChange,
  locked,
  saving = false,
  onBack,
  onContinue,
}: ShutdownRuleStepProps) => {
  const [draft, setDraft] = useState(settings);

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  const update = (patch) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      onChange(next);
      return next;
    });
  };
  const isThresholdInvalid =
    draft.mode === 'threshold_custom' &&
    (draft.customThreshold == null || draft.customThreshold < 76 || draft.customThreshold > 100);
  const isHoursInvalid = draft.mode === 'time_based' && (draft.hours == null || draft.hours < 1 || draft.hours > 720);
  const disableContinue = saving || (!locked && (isThresholdInvalid || isHoursInvalid));

  const summary =
    draft.mode === 'threshold_75'
      ? 'This room will auto-close when approvals reach 75% of eligible shares.'
      : draft.mode === 'threshold_custom'
        ? `This room will auto-close when approvals reach ${draft.customThreshold ?? 90}% of eligible shares.`
        : `This room will auto-close ${draft.hours ?? 48} hour${(draft.hours ?? 48) === 1 ? '' : 's'} after launch.`;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5 space-y-4">
        <div>
          <p className="text-xs font-semibold text-gray-900">Shutdown Rule</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Choose when this Vote Room should close automatically. This is locked after launch.
          </p>
        </div>

        <ShutdownRuleForm
          mode={draft.mode}
          customThreshold={draft.customThreshold ?? 90}
          hours={draft.hours ?? 48}
          readOnly={locked}
          onModeChange={(mode) => update({ mode })}
          onCustomThresholdChange={(customThreshold) => update({ customThreshold })}
          onHoursChange={(hours) => update({ hours })}
        />

        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <div>
            <p className="text-[11px] font-semibold text-amber-900">Shutdown Rule Required</p>
            <p className="text-[11px] text-amber-900/90">{summary}</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-2 rounded-md text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ChevronRight size={13} className="mr-1 rotate-180" /> Back to Review
          </button>
          <button
            type="button"
            onClick={() => onContinue(draft)}
            disabled={disableContinue}
            className="inline-flex items-center px-4 py-2 rounded-md text-xs font-semibold bg-[#0f172a] text-white hover:bg-[#1e293b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Continue to Launch'} <ChevronRight size={13} className="ml-1.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
